import { useQueryAssets } from '@/hooks/useAssets';
import { Asset } from '@/types';

/**
 * Custom hook for fetching and managing asset data with pagination.
 * Provides asset data, loading states, pagination controls, and filter management.
 *
 * @param parentFilters - Optional filters to apply to the asset query
 * @param maxNodes - Maximum number of asset nodes to fetch (default: 1000)
 * @param enabled - Whether to enable the query (default: true)
 * @returns Object containing assets, loading state, pagination controls, and filter management
 */
export const useAssetData = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parentFilters?: any,
  maxNodes: number = 1000,
  enabled: boolean = true
) => {
  const {
    data: assets,
    isLoading: assetsIsLoading,
    hasNextPage,
    fetchNextPage,
    filters: assetFilters,
    setFilters: setAssetFilters,
  } = useQueryAssets({
    limit: maxNodes,
    parentFilters: parentFilters,
    enabled,
  });

  return {
    /** Array of fetched assets */
    assets: assets as Asset[],
    /** Loading state for initial asset fetch */
    isLoading: assetsIsLoading,
    /** Whether more assets are available to load */
    hasNextPage,
    /** Function to fetch the next page of assets */
    fetchNextPage,
    /** Current filter configuration */
    assetFilters,
    /** Function to update asset filters */
    setAssetFilters,
  };
};
